import React, {useEffect, useMemo, useRef} from 'react';
import Layout from "@/components/layout/main";
import {useTranslation} from "next-i18next";
import {Fetch, ModifyURL} from "@/base/utils/fetch";
import {CustomPage} from "@/base/types/CustomPage";
import PageStyles from '@/base/styles/BootstrapBridge.module.scss';
import MetaBlock from "@/base/components/MetaBlock";
import {getRegionConfig} from "@/base/utils/regions";
import {Heading, RenderPage} from "@/base/components/page/RenderPage";
import {serialize} from "next-mdx-remote/serialize";
import {useRouter} from "next/router";
import {CustomPageSimple} from "@/base/types/CustomPageSimple";
import {PageOutput} from "@/base/types/Pagination";
import {Affix, ConfigProvider, Image, Menu} from "antd";
import {MenuProps} from "antd/es/menu";
import Link from "next/link";
import {useHashState} from "@/base/utils/hash.util";
import {DelayedEffectHook} from "@/base/types/delayed-effect.hook";
import {MAX_LAPTOP, useMediaQuery} from "@/base/utils/media.hook";
import {compareObjects} from "@/base/utils/compare.util";
import useFNSLocale from "@/base/utils/datefnslocale";
import EyeOutlined from "@ant-design/icons/EyeOutlined";
import {format} from "date-fns/format";
import {escapeString, getUrl} from "@/base/utils/url";
import formatToParam from "@/base/utils/date.format";
import Head from "next/head";
import ImageWithFallback from "@/base/components/util/ImageWithFallback";
import {getImageUrl} from "@/base/utils/page/page.utils";
import {IoCalendarClearOutline} from "react-icons/io5";


type MenuItem = Required<MenuProps>['items'][number] & {
	children: MenuItem[],
}

export type PageProps = {
	page: {
		slug: string,
		output: CustomPage | null,
	}
};

export const getPagesList = async (locale: string, site?: string, prefix?: string, maxPages: number = 100): Promise<CustomPageSimple[]> => {
	return Fetch({
		url: ModifyURL(`/pages/list`),
		locale: locale,
		params: {
			page: 1,
			perPage: maxPages,
			site: site,
			path: prefix
		}
	}).then((res: {
		data: PageOutput<CustomPageSimple>
	}) => {
		if (!res || !res.data) return [];

		return res.data.data || [];
	}).catch((err) => {
		console.error(err);

		return [];
	});
}

export const FetchPage = async (slug: string | string[], locale: string, site?: string): Promise<CustomPage | undefined> => {
	if (typeof slug === 'object')
	{
		slug = slug.join('/');
	}

	return Fetch({
		url: ModifyURL(`/pages/view/${slug}`),
		locale: locale,
		params: {
			site: site
		}
	}).then(async (res: {
		data: CustomPage
	}) => {
		if (!res || !res.data) return undefined;

		const output = res?.data;
		output.content = oldToNewContent(output.content);

		if (output.format === 'md')
		{
			output.content_md = await serialize(output.content);
			output.content = '';
		}

		return output;
	}).catch((err) => {
		console.error(err);

		return undefined;
	});
}

const oldToNewContent = (str: string): string => {
	let output = str;

	output = output.replace(/https:\/\/stableproxy.com\//g, '/');
	// all img src="/ prepand api.stableproxy.com
	output = output.replace(/src="\/(.*?)"/g, 'src="https://api.stableproxy.com/$1"');
	// do same what with src img but with markdown, but remove https:// and replace with /
	// output = output.replace(/!\[(.*?)\]\((.*?)\)/g, '![$1](https://api.stableproxy.com/$2)');
	// replace to Image
	// output = output.replace(/<img(.*?)>/g, '<Image$1/>');
	// remove <a who has .sp-media-preview and replace with own content
	// output = output.replace(/<a(.*?)class="sp-media-preview"(.*?)>(.*?)<\/a>/g, '$3');


	return output;
}

type TreeHeading = Heading & {
	children: TreeHeading[]
}

export const getNavigationItems = (tree: TreeHeading[] = []): [MenuItem[], Record<string, string>] => {
	const items: MenuItem[] = [];
	let k = 0;
	const _openKeys: Record<string, string> = {};
	console.debug("upd")

	const treeToMenu = (tree: TreeHeading[]): MenuItem[] => tree?.map((item) => {
		const v = treeToMenu(item.children);
		const _k = (k++).toString();
		_openKeys[item.url] = _k;
		return {
			key: _k,
			className: `h-auto`,
			icon: undefined,
			label: <Link shallow={true} scroll={false} replace={true} as={undefined} href={{hash: item.url}} className={`!w-full !h-full whitespace-normal !text-color`}>{item.text}</Link>,
			showArrow: false,
			children: v.length > 0 ? v : undefined
		} as MenuItem;
	}) || [];

	if (tree)
	{
		items.push(...treeToMenu(tree));
	}

	return [items, _openKeys];
}

export const NavigationMenu = ({tree, selectedUrl}: {
	tree: TreeHeading[],
	selectedUrl?: string
}) => {
	const [headings, openKeys] = getNavigationItems(tree);
	const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);

	useEffect(() => {
		const toInsert = [];
		if (selectedUrl && openKeys[selectedUrl])
		{
			toInsert.push(openKeys[selectedUrl]);
		}

		if (!compareObjects(selectedKeys, toInsert))
		{
			setSelectedKeys(toInsert);
			console.debug("X")
		}
	}, [selectedUrl, openKeys]);
	console.debug(selectedKeys, openKeys)

	return <ConfigProvider theme={{
		components: {
			Menu: {
				itemHeight: 40,
			},
		}
	}}>
		<Menu
			mode="inline"
			theme="light"
			className={`!bg-main-bg shadow-lg overflow-hidden shadow-gray-400/20 rounded-xl 
					[&_.ant-menu-submenu-title]:!h-auto
					[&_.ant-menu-item]:!h-auto
					[&_.ant-menu-submenu-selected]:!bg-primary-100/50
					[&_.ant-menu-item-selected]:!bg-primary/10
					[&_.ant-menu-submenu-title_.ant-menu-submenu-arrow]:after:!content-none [&_.ant-menu-submenu-title_.ant-menu-submenu-arrow]:before:!content-none`}
			items={headings}
			openKeys={Object.values(openKeys)}
			disabledOverflow={false}
			selectedKeys={selectedKeys}
			onOpenChange={(keys) => {
				// setOpenKeys(keys);
			}}
		>
		</Menu>
	</ConfigProvider>
}

export const RenderContent = ({page, onHeadings}: {
	page: PageProps['page'],
	onHeadings?: (headings: Heading[]) => Promise<void>
}) => {
	return <>
		{page.output && RenderPage(page.output, onHeadings)}
	</>
}

export default function Page({page}: PageProps) {
	const {t} = useTranslation();
	const router = useRouter();
	const IS_MOBILE = useMediaQuery(MAX_LAPTOP);
	const [hashState] = useHashState();
	const timeoutRef = useRef<any>(null);
	const [currentViewHash, setCurrentViewHash] = React.useState<Heading | null>(null);
	const fnsLocale = useFNSLocale();

	if (!page?.output)
	{
		return null;
	}

	const [headings, setHeadings] = React.useState<Heading[]>([]);

	const regionConfig = getRegionConfig();
	const isMd = page.output.format === 'md';

	DelayedEffectHook(() => {
		if (!hashState)
		{
			return;
		}

		const hash = hashState.replace('#', '');

		const found = headings.find((item) => decodeURIComponent(item.url).toLowerCase().trim() == decodeURIComponent(hash).toLowerCase().trim());

		if (found && found.ref?.current && window)
		{
			const positionTop = window.scrollY + found.ref.current.getBoundingClientRect().top;
			console.debug(positionTop, found.url, found.ref)
			window.scrollTo({
				top: positionTop - 95,
				behavior: 'smooth'
			});
		}
	}, [hashState, headings], 150);

	useEffect(() => {
		if (timeoutRef.current)
		{
			clearTimeout(timeoutRef.current);
		}

		if (headings.length > 0)
		{
			timeoutRef.current = setInterval(() => {
				const found = headings.find((item) => {
					if (!item.ref?.current)
					{
						return false;
					}

					const headerHeight = 80;
					const positionTop = item.ref.current.getBoundingClientRect().top - headerHeight;
					if (positionTop > 0 && positionTop < window.innerHeight - headerHeight)
					{
						return true;
					}

					return false;
				});

				if (found && found != currentViewHash)
				{
					setCurrentViewHash(found);
				}
			}, 500);
		}

		return () => {
			if (timeoutRef.current)
			{
				clearTimeout(timeoutRef.current);
			}
		}
	}, [headings]);


	const RenderedContent = useMemo(() => {
		return <RenderContent page={page} onHeadings={async (headings) => setHeadings(headings)}/>
	}, [page, router.locale, router.query.slug]);

	const tree: TreeHeading[] = [];

	const minimLevel = Math.min(...headings.map((h) => h.level));
	headings.forEach((heading) => {
		const item: TreeHeading = {
			...heading,
			children: []
		};

		if (heading.level === minimLevel)
		{
			tree.push(item);
		}
		else
		{
			const parent = tree[tree.length - 1];
			if (!parent)
			{
				return;
			}
			parent.children.push(item);
		}
	});

	const RenderedNavigation = <div className={`flex flex-col gap-4`}>
		{tree.length > 0 && <NavigationMenu tree={tree} selectedUrl={currentViewHash?.url}/>}
		<div className={`bg-main-bg p-7 rounded-xl flex flex-wrap`}>
			<p className={`flex-auto`}>{page?.output?.views} <EyeOutlined/></p>
			<p>{format(new Date(page?.output?.created_at), 'dd MMMM yyyy', {locale: fnsLocale})}</p>
		</div>
	</div>;

	return <>
		<MetaBlock
			title={page?.output?.title || t('base-landing:meta.title-page')}
			description={page?.output?.description || t('base-landing:meta.description-page', {
				in: t(regionConfig.tKey('in'))
			})}
		/>
		<Head>
			<script type='application/ld+json' dangerouslySetInnerHTML={{
				__html: `
			{
				"@context": "https://schema.org",
				"@type": "${page?.output?.url.includes("blog") ? "BlogPosting" : "Article"}",
				"mainEntityOfPage": {
					"@type": "WebPage",
					"@id": "${getUrl(`/p/${page?.output?.url}`, router)}"
				},
				"headline": "${escapeString(page?.output?.h1)}",
				"description": "${escapeString(page?.output?.description)}",
				${page?.output?.image_url && `"image": "${page?.output?.image_url}",`}
				"author": {
					"@type": "Person",
					"name": "${escapeString(page?.output?.user?.name || 'Admin')}"
				},
				"publisher": {
					"@type": "Organization",
					"name": "${process.env.NEXT_PUBLIC_APP_NAME || 'Site'}",
					"logo": {
						"@type": "ImageObject",
						"url": "${process.env.NEXT_PUBLIC_APP_URL || 'http://localhost'}/image/android-chrome-512x512.png"
					}
				},
				"datePublished": "${formatToParam(new Date(page?.output?.created_at))}",
				"dateModified": "${formatToParam(new Date(page?.output?.updated_at))}"
			}
		`
			}}/>
		</Head>
		{isMd ? <div className={`mx-auto grid grid-cols-8 xl:grid-cols-12 gap-4 gap-y-16 md:gap-y-20 lg:gap-x-8 max-2xl:px-6 2xl:container relative lg:max-w-screen-lxl xl:max-w-[1280px]`}>
			<div className={`col-span-full`}>
			</div>
			<div className={`col-span-full`}>
				{page.output.image_url && (
					<>
						<ImageWithFallback
							src={getImageUrl(page.output.image_url)}
							fallbackSrc={`/image/no-picture.png`}
							alt={page.output?.title}
							width={200}
							height={200}
							layout={`responsive`}
							className={`rounded-xl object-contain left-0 top-0 -mt-[70px] max-h-[50vh] opacity-5 min-h-[200px] mb-10 absolute blur-md`}
						/>
						<ImageWithFallback
							src={getImageUrl(page.output.image_url)}
							fallbackSrc={`/image/no-picture.png`}
							alt={page.output?.title}
							width={200}
							height={200}
							layout={`responsive`}
							className={`rounded-xl object-contain relative max-h-[17vh] min-h-[200px] mb-10`}
						/>
					</>
				)}
				<h1 className={`!font-bold text-3xl text-text-2 text-center my-4 relative`}>
					{page.output?.h1}
				</h1>
				{page.output?.description && page.output?.description.length > 2 && <p className={`!font-medium text-center my-4 relative`}>
					{page.output?.description}
				</p>}
				{page.output.user && (
					<div className={`p-7 rounded-xl flex flex-wrap items-center justify-center gap-6`}>
						<div className={`flex gap-4 items-center justify-center`}>
							<Image
								src={page.output.user.avatar_url || '/image/no-picture.png'}
								alt={page.output.user.name}
								width={50}
								height={50}
								className={`rounded-full shadow-md`}
							/>
							<p className={`font-medium`}>{page.output.user.name}</p>
						</div>
						<p className={`flex items-center justify-center gap-2`}>
							<IoCalendarClearOutline/>
							{format(new Date(page.output.created_at), 'dd MMMM yyyy', {locale: fnsLocale})}
						</p>
						<p className={`flex items-center justify-center gap-2`}>
							<EyeOutlined/> {page.output.views}
						</p>
					</div>
				)}
			</div>
			<div className={`col-span-full lg:col-span-2 xl:col-span-4 overflow-hidden rounded-xl relative`}>
				{IS_MOBILE ? <>
					{RenderedNavigation}
				</> : <Affix offsetTop={100}>
					{RenderedNavigation}
				</Affix>}
			</div>
			<div className={`col-span-full lg:col-span-6 xl:col-span-8 overflow-hidden rounded-xl bg-main-bg px-7 py-6 relative`}>
				<div className={`[&>*:first-child]:!mt-0 [&_img]:!max-w-full [&_img]:!w-full [&_.ant-image]:!w-full`}>
					{RenderedContent}
				</div>
			</div>
		</div> : <div className={`grid gap-4 gap-y-16 md:gap-y-24 container my-5 mx-auto`}>
			<div className={`col-span-full bg-main-bg shadow-lg overflow-hidden shadow-gray-400/20 rounded-xl`}>
				<div className={`bg-primary text-white text-center p-6`}>
					<h1 className={`font-montserrat-alternates !font-medium my-4`}>
						{page.output?.h1}
					</h1>
				</div>
				<div className={`p-4 ${PageStyles.bootstrap}`} style={{
					// '--text-color-2': '#bbb
				}}>
					{RenderedContent}
				</div>
			</div>
		</div>}
	</>
};

Page.getLayout = function getLayout(page: any) {
	return <Layout rootClassName={`!bg-gradient-to-b from-zinc-50/50 dark:from-stone-50 dark:to-stone-100 to-transparent`}>
		{page}
	</Layout>;
};